<template>
  <div class="admin-billings-view container py-4">
    <!-- Breadcrumbs -->
    <nav aria-label="breadcrumb" class="mb-3">
      <ol class="breadcrumb">
        <li class="breadcrumb-item">
          <router-link to="/admin" class="text-decoration-none">
            <font-awesome-icon icon="home" class="me-1" />
            Admin
          </router-link>
        </li>
        <li class="breadcrumb-item active" aria-current="page">Rechnungsverwaltung</li>
      </ol>
    </nav>

    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div class="d-flex align-items-center">
        <div>
          <h2 class="mb-0">
            <font-awesome-icon icon="file-invoice-dollar" class="me-2" />
            Rechnungs-Verwaltung
          </h2>
          <p class="text-muted mb-0">
            Alle Rechnungen im System verwalten (Quittung-Käufe & Team-Upgrades)
          </p>
        </div>
      </div>
      <BackButton fallback="admin" />
    </div>

    <!-- Filters -->
    <div class="card mb-4">
      <div class="card-header">
        <h5 class="mb-0">
          <font-awesome-icon icon="filter" class="me-2" />
          Filter & Suche
        </h5>
      </div>
      <div class="card-body">
        <!-- First Row: Basic Filters -->
        <div class="row g-3 mb-3">
          <div class="col-md-3">
            <label class="form-label">Status</label>
            <multiselect
              v-model="filters.status"
              :options="statusOptions"
              :searchable="true"
              :close-on-select="true"
              :use-teleport="true"
              :show-labels="false"
              placeholder="Status auswählen..."
              label="label"
              track-by="value"
              :no-options="'Keine Status verfügbar'"
              :no-results="'Keine Status gefunden'"
            />
          </div>

          <div class="col-md-3">
            <label class="form-label">Typ</label>
            <multiselect
              v-model="filters.type"
              :options="typeOptions"
              :searchable="true"
              :close-on-select="true"
              :use-teleport="true"
              :show-labels="false"
              placeholder="Typ auswählen..."
              label="label"
              track-by="value"
              :no-options="'Keine Typen verfügbar'"
              :no-results="'Keine Typen gefunden'"
            />
          </div>

          <div class="col-md-3">
            <label class="form-label">Datum von</label>
            <input v-model="filters.dateFrom" type="date" class="form-control" />
          </div>

          <div class="col-md-3">
            <label class="form-label">Datum bis</label>
            <input v-model="filters.dateTo" type="date" class="form-control" />
          </div>
        </div>

        <!-- Second Row: Amount Filters and Search -->
        <div class="row g-3 mb-3">
          <div class="col-md-3">
            <label class="form-label">Dokumenttyp</label>
            <multiselect
              v-model="filters.documentType"
              :options="documentTypeOptions"
              :searchable="true"
              :close-on-select="true"
              :use-teleport="true"
              :show-labels="false"
              placeholder="Dokumenttyp auswählen..."
              label="label"
              track-by="value"
              :no-options="'Keine Typen verfügbar'"
              :no-results="'Keine Typen gefunden'"
            />
          </div>
          <div class="col-md-3">
            <label class="form-label">Betrag min (€)</label>
            <input
              v-model="filters.amountMin"
              type="number"
              class="form-control"
              placeholder="0"
              step="0.01"
            />
          </div>

          <div class="col-md-3">
            <label class="form-label">Betrag max (€)</label>
            <input
              v-model="filters.amountMax"
              type="number"
              class="form-control"
              placeholder="1000"
              step="0.01"
            />
          </div>

          <div class="col-md-3">
            <label class="form-label">Team</label>
            <multiselect
              v-model="filters.teamId"
              :options="teamOptions"
              :searchable="true"
              :close-on-select="true"
              :use-teleport="true"
              :show-labels="false"
              placeholder="Team auswählen..."
              label="name"
              track-by="id"
              :no-options="'Keine Teams verfügbar'"
              :no-results="'Keine Teams gefunden'"
            />
          </div>

          <div class="col-md-3">
            <label class="form-label">Suche</label>
            <input
              v-model="filters.search"
              type="text"
              class="form-control"
              placeholder="Rechnungsnummer/Zahlungsaufforderung, Name, E-Mail, Unternehmen..."
            />
          </div>
        </div>

        <!-- Third Row: Action Buttons -->
        <div class="row">
          <div class="col-12">
            <div class="d-flex gap-2 flex-wrap">
              <button @click="loadBillings" class="btn btn-primary" :disabled="loading">
                <font-awesome-icon icon="search" class="me-2" />
                Suchen
              </button>
              <button @click="resetFilters" class="btn btn-outline-secondary">
                <font-awesome-icon icon="refresh" class="me-2" />
                Zurücksetzen
              </button>
              <div class="vr mx-2"></div>
              <button @click="exportExcel" class="btn btn-success" :disabled="loading">
                <font-awesome-icon icon="file-excel" class="me-2" />
                Excel Export
              </button>
              <button @click="exportZip" class="btn btn-warning" :disabled="loading">
                <font-awesome-icon icon="file-archive" class="me-2" />
                ZIP Export
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center my-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Daten werden geladen...</span>
      </div>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="alert alert-danger" role="alert">
      <font-awesome-icon icon="exclamation-triangle" class="me-2" />
      {{ error }}
    </div>

    <!-- No Data -->
    <div v-else-if="!billings || billings.length === 0" class="text-center py-5">
      <div class="text-muted">
        <font-awesome-icon icon="file-invoice-dollar" class="h1 mb-3" />
        <p>Keine Dokumente (Rechnungen/Zahlungsaufforderungen) gefunden.</p>
        <p class="small">Versuche andere Filter-Einstellungen.</p>
      </div>
    </div>

    <!-- Billings List -->
    <div v-else class="card">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="mb-0">
          <font-awesome-icon icon="list" class="me-2" />
          Rechnungen & Zahlungsaufforderungen ({{ pagination?.total || 0 }})
        </h5>
        <div class="text-muted small">
          Seite {{ pagination?.page || 1 }} von {{ pagination?.totalPages || 1 }}
        </div>
      </div>
      <div class="table-responsive">
        <table class="table table-hover mb-0">
          <thead class="table-light">
            <tr>
              <th style="width: 12%">Nummer</th>
              <th style="width: 8%">Typ</th>
              <th style="width: 20%">Kunde</th>
              <th style="width: 10%">Betrag</th>
              <th style="width: 8%">Zahlung</th>
              <th style="width: 8%">Status</th>
              <th style="width: 8%">Korrektur</th>
              <th style="width: 12%">Datum</th>
              <th style="width: 22%">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="billing in billings"
              :key="`${billing.type}-${billing.id}`"
              class="align-middle"
            >
              <td>
                <div class="fw-bold text-primary">{{ billing.invoiceNumber || billing.reminderInvoiceNumber || '–' }}</div>
                <div class="text-muted small">{{ getDocumentLabel(billing) }}</div>
              </td>
              <td>
                <span :class="getTypeBadgeClass(billing.type)" class="badge fs-6">
                  {{ getTypeDisplayName(billing.type) }}
                </span>
              </td>
              <td>
                <div>
                  <div class="fw-semibold">{{ billing.customerName }}</div>
                  <div class="text-muted small">{{ billing.customerEmail }}</div>
                </div>
              </td>
              <td>
                <div class="fw-bold text-success">{{ formatCurrency(billing.price || billing.amount) }}</div>
              </td>
              <td>
                <div class="text-center">
                  <font-awesome-icon 
                    :icon="getPaymentMethodIcon(billing.method || billing.paymentMethod)" 
                    :class="getPaymentMethodClass(billing.method || billing.paymentMethod)"
                    :title="getPaymentMethodTitle(billing.method || billing.paymentMethod)"
                    class="fs-5"
                  />
                </div>
              </td>
              <td>
                <span :class="getStatusBadgeClass(billing.status)" class="badge fs-6">
                  {{ getStatusDisplayName(billing.status) }}
                </span>
              </td>
              <td>
                <span v-if="hasCreditNote(billing)" class="badge bg-warning text-dark">
                  Ja
                </span>
                <span v-else class="text-muted">–</span>
              </td>
              <td>
                <div class="text-muted">{{ formatDate(billing.createdAt) }}</div>
              </td>
              <td>
                <div class="btn-group btn-group-sm">
                  <button
                    class="btn btn-outline-primary"
                    @click="downloadPDF(billing.id, billing.type)"
                    :disabled="loading"
                    title="PDF herunterladen"
                  >
                    <font-awesome-icon icon="download" />
                  </button>

                  <button
                    v-if="hasCreditNote(billing)"
                    class="btn btn-outline-secondary"
                    @click="downloadCreditPDF(billing)"
                    :disabled="loading"
                    title="Storno/Gutschrift PDF herunterladen"
                  >
                    <font-awesome-icon icon="file-invoice" />
                  </button>

                  <button
                    class="btn btn-outline-secondary"
                    @click="recreateInvoice(billing)"
                    :disabled="loading"
                    title="Dokumente neu erzeugen"
                  >
                <font-awesome-icon icon="refresh" />
              </button>
                  
                  <button
                    v-if="(billing.method === 'bank_transfer' || billing.paymentMethod === 'bank_transfer') && billing.status !== 'completed'"
                    class="btn btn-outline-success"
                    @click="markAsPaid(billing)"
                    :disabled="loading"
                    title="Als bezahlt markieren"
                  >
                    <font-awesome-icon icon="check" />
                  </button>
                  
                  <button
                    v-if="canCancelBilling(billing)"
                    class="btn btn-outline-secondary"
                    @click="cancelBilling(billing)"
                    :disabled="loading"
                    title="Zahlungsaufforderung stornieren"
                  >
                    <font-awesome-icon icon="ban" />
                  </button>

              <button
                v-if="billing.status === 'completed'"
                class="btn btn-outline-danger"
                @click="cancelInvoiceWithCredit(billing)"
                :disabled="loading"
                title="Rechnung stornieren und Gutschrift erzeugen"
              >
                <font-awesome-icon icon="file-invoice-dollar" />
              </button>
                  
                  <button
                    v-if="(billing.method === 'paypal' || billing.paymentMethod === 'paypal') && billing.status !== 'completed'"
                    class="btn btn-outline-warning"
                    @click="recheckPayPal(billing)"
                    :disabled="loading"
                    title="PayPal überprüfen"
                  >
                    <font-awesome-icon icon="fab fa-paypal" />
                  </button>

                  <button
                    v-if="billing.status === 'failed'"
                    class="btn btn-outline-danger"
                    @click="deleteBilling(billing)"
                    :disabled="loading"
                    title="Fehlgeschlagene Rechnung löschen"
                  >
                    <font-awesome-icon icon="trash" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="pagination && pagination.totalPages > 1" class="d-flex justify-content-center mt-4">
      <nav aria-label="Billing Pagination">
        <ul class="pagination">
          <li class="page-item" :class="{ disabled: pagination.page <= 1 }">
            <button
              class="page-link"
              @click="changePage(pagination.page - 1)"
              :disabled="pagination.page <= 1"
            >
              Zurück
            </button>
          </li>

          <li
            v-for="page in getPageNumbers()"
            :key="page"
            class="page-item"
            :class="{ active: page === pagination.page }"
          >
            <button class="page-link" @click="changePage(page)">
              {{ page }}
            </button>
          </li>

          <li class="page-item" :class="{ disabled: pagination.page >= pagination.totalPages }">
            <button
              class="page-link"
              @click="changePage(pagination.page + 1)"
              :disabled="pagination.page >= pagination.totalPages"
            >
              Weiter
            </button>
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { adminApi } from '@/api/admin'
import { teamService } from '@/api/teams'
import { downloadBillingPDF } from '@/api/admin'
import { showSuccess, showError, showConfirmDialog } from '@/services/notification'
import Multiselect from 'vue-multiselect'

const loading = ref(false)
const error = ref(null)
const billings = ref([])
const pagination = ref(null)
const teams = ref([])

// Options for multiselects
const statusOptions = [
  { value: 'pending', label: 'Ausstehend' },
  { value: 'completed', label: 'Abgeschlossen' },
  { value: 'failed', label: 'Fehlgeschlagen' },
  { value: 'cancelled', label: 'Storniert' }
]

const typeOptions = [
  { value: 'coin', label: 'Quittung-Kauf' },
  { value: 'team', label: 'Team-Upgrade' }
]

const documentTypeOptions = [
  { value: 'invoice', label: 'Rechnung' },
  { value: 'payment_request', label: 'Zahlungsaufforderung' }
]

const teamOptions = computed(() => {
  return teams.value.map(team => ({
    id: team.id,
    name: team.name
  }))
})

const filters = ref({
  status: null,
  type: null,
  documentType: null,
  dateFrom: '',
  dateTo: '',
  amountMin: '',
  amountMax: '',
  search: '',
  teamId: null,
})

const loadBillings = async () => {
  loading.value = true
  error.value = null

  try {
    const params = {
      page: pagination.value?.page || 1,
      limit: 20,
    }

    if (filters.value.status && filters.value.status.value) {
      params.status = filters.value.status.value
    }
    if (filters.value.type && filters.value.type.value) {
      params.type = filters.value.type.value
    }
    if (filters.value.documentType && filters.value.documentType.value) {
      params.documentType = filters.value.documentType.value
    }
    if (filters.value.dateFrom) {
      params.dateFrom = filters.value.dateFrom
    }
    if (filters.value.dateTo) {
      params.dateTo = filters.value.dateTo
    }
    if (filters.value.amountMin) {
      params.amountMin = filters.value.amountMin
    }
    if (filters.value.amountMax) {
      params.amountMax = filters.value.amountMax
    }
    if (filters.value.search) {
      params.search = filters.value.search
    }
    if (filters.value.teamId && filters.value.teamId.id) {
      params.teamId = filters.value.teamId.id
    }

    const billingsResult = await adminApi.getAllBillings(params)

    billings.value = billingsResult.billings || []
    pagination.value = billingsResult.pagination || null
  } catch (err) {
    console.error('Error loading billings:', err)
    error.value = err.response?.data?.message || 'Fehler beim Laden der Dokumente'
  } finally {
    loading.value = false
  }
}

const resetFilters = () => {
  filters.value = {
    status: null,
    type: null,
    documentType: null,
    dateFrom: '',
    dateTo: '',
    amountMin: '',
    amountMax: '',
    search: '',
    teamId: null,
  }
  loadBillings()
}

const changePage = (page) => {
  if (pagination.value) {
    pagination.value.page = page
    loadBillings()
  }
}

const getPageNumbers = () => {
  if (!pagination.value) return []

  const pages = []
  const current = pagination.value.page
  const total = pagination.value.totalPages

  let start = Math.max(1, current - 2)
  let end = Math.min(total, current + 2)

  if (end - start < 4) {
    if (start === 1) {
      end = Math.min(total, start + 4)
    } else {
      start = Math.max(1, end - 4)
    }
  }

  for (let i = start; i <= end; i++) {
    pages.push(i)
  }

  return pages
}

const downloadPDF = async (billingId, type) => {
  try {
    loading.value = true
    let response

    if (type === 'team') {
      
      response = await teamService.downloadTeamBillingPDF(billingId)
    } else {
      
              response = await downloadBillingPDF(billingId)
    }

    
    const contentDisposition = response.headers?.['content-disposition']
    let filename = `rechnung${billingId ? '-'+ billingId : ''}.pdf` 
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '')
      }
    }

    const url = window.URL.createObjectURL(response.data)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    showSuccess('PDF erfolgreich heruntergeladen')
  } catch (err) {
    console.error('Error downloading PDF:', err)
    showError('Fehler beim Herunterladen der PDF')
  } finally {
    loading.value = false
  }
}

const exportExcel = async () => {
  try {
    const blob = await adminApi.exportBillingsExcel({
      ...filters.value,
      includeCredits: true
    })

    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `rechnungen_export_${new Date().toISOString().split('T')[0]}.xlsx`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    showSuccess('Excel-Export erfolgreich heruntergeladen')
  } catch (err) {
    console.error('Error exporting Excel:', err)
    showError('Fehler beim Excel-Export')
  }
}

const exportZip = async () => {
  try {
    const blob = await adminApi.exportBillingsZip({
      ...filters.value,
      includeCredits: true
    })

    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `alle_rechnungen_export_${new Date().toISOString().split('T')[0]}.zip`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    showSuccess('ZIP-Export erfolgreich heruntergeladen')
  } catch (err) {
    console.error('Error exporting ZIP:', err)
    showError('Fehler beim ZIP-Export')
  }
}

const recreateInvoice = async (billing) => {
  const documentLabel = getDocumentLabel(billing)
  const hasCredit = hasCreditNote(billing)
  const result = await showConfirmDialog(
    'Dokumente neu erzeugen',
    `Möchtest du die Dokumente wirklich neu erzeugen?\n\n` +
      `Dokument: ${documentLabel}${hasCredit ? ' + Korrekturrechnung' : ''}\n` +
      `Nummer: ${billing.invoiceNumber || billing.reminderInvoiceNumber || 'Vorschau'}`,
    'Ja, neu erzeugen',
    'Abbrechen',
    { icon: 'question' }
  )

  if (!result.isConfirmed) return

  try {
    loading.value = true
    const apiBillingType = billing.type === 'coin' ? 'billing' : 'teamBilling'
    const res = await adminApi.recreateBillingInvoice(billing.id, apiBillingType)
    if (res.success) {
      showSuccess(res.message || 'Rechnung neu erzeugt')
      await loadBillings()
    } else {
      showError(res.message || 'Fehler beim Neu-Erzeugen der Rechnung')
    }
  } catch (err) {
    console.error('Error recreating invoice:', err)
    showError(err.response?.data?.message || 'Fehler beim Neu-Erzeugen der Rechnung')
  } finally {
    loading.value = false
  }
}

const cancelInvoiceWithCredit = async (billing) => {
  const amount = billing.price || billing.amount
  const result = await showConfirmDialog(
    'Rechnung stornieren',
    `Möchtest du diese Rechnung wirklich stornieren und eine Gutschrift erzeugen?\n\n` +
      `Betrag: ${formatCurrency(amount)}\n` +
      `Nummer: ${billing.invoiceNumber || '–'}`,
    'Ja, stornieren',
    'Abbrechen',
    {
      icon: 'warning',
      confirmButtonColor: '#dc3545'
    }
  )

  if (!result.isConfirmed) return

  try {
    loading.value = true
    const apiBillingType = billing.type === 'coin' ? 'billing' : 'teamBilling'
    const res = await adminApi.cancelInvoiceWithCredit(billing.id, apiBillingType)
    if (res.success) {
      showSuccess(res.message || 'Gutschrift erzeugt und Rechnung storniert')
      await loadBillings()
    } else {
      showError(res.message || 'Fehler beim Stornieren')
    }
  } catch (err) {
    console.error('Error cancelling invoice with credit:', err)
    showError(err.response?.data?.message || 'Fehler beim Stornieren')
  } finally {
    loading.value = false
  }
}

const hasCreditNote = (billing) => {
  return Boolean(billing?.paymentDetails?.creditNote?.pdfPath) || billing.status === 'cancelled'
}

const downloadCreditPDF = async (billing) => {
  try {
    loading.value = true
    let response
    if (billing.type === 'team') {
      response = await adminApi.downloadTeamBillingCreditPDF(billing.id)
    } else {
      response = await adminApi.downloadBillingCreditPDF(billing.id)
    }

    const contentDisposition = response.headers?.['content-disposition']
    let filename = `gutschrift${billing.invoiceNumber ? '-' + billing.invoiceNumber : ''}.pdf`
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '')
      }
    }

    const url = window.URL.createObjectURL(response.data)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    showSuccess('Storno/Gutschrift-PDF erfolgreich heruntergeladen')
  } catch (err) {
    console.error('Error downloading credit PDF:', err)
    showError('Fehler beim Herunterladen der Storno/Gutschrift-PDF')
  } finally {
    loading.value = false
  }
}

const markAsPaid = async (billing) => {
  const billingType = billing.type === 'coin' ? 'Quittung-Kauf' : 'Team-Upgrade'
  const amount = billing.price || billing.amount
  const documentLabel = getDocumentLabel(billing)
  
  const result = await showConfirmDialog(
    'Als bezahlt markieren',
    `Möchtest du diese ${documentLabel} wirklich als bezahlt markieren?\n\n` +
    `Typ: ${billingType}\n` +
    `Dokumenttyp: ${documentLabel}\n` +
    `Betrag: ${formatCurrency(amount)}\n` +
    `Nummer: ${billing.invoiceNumber || billing.reminderInvoiceNumber || 'Vorschau'}`,
    'Ja, als bezahlt markieren',
    'Abbrechen',
    {
      icon: 'question',
      confirmButtonColor: '#28a745'
    }
  )
  
  if (result.isConfirmed) {
    try {
      loading.value = true
      
      const apiBillingType = billing.type === 'coin' ? 'billing' : 'teamBilling'
      const apiResult = await adminApi.markBillingAsPaid(billing.id, apiBillingType)
      
      if (apiResult.success) {
        showSuccess(apiResult.message)
        await loadBillings()
      } else {
        showError(apiResult.message || 'Fehler beim Markieren als bezahlt')
      }
    } catch (error) {
      console.error('Error marking as paid:', error)
      showError(error.response?.data?.message || 'Fehler beim Markieren als bezahlt')
    } finally {
      loading.value = false
    }
  }
}

const cancelBilling = async (billing) => {
  const billingType = billing.type === 'coin' ? 'Quittung-Kauf' : 'Team-Upgrade'
  const amount = billing.price || billing.amount
  const documentLabel = getDocumentLabel(billing)

  const result = await showConfirmDialog(
    'Zahlungsaufforderung stornieren',
    `Möchtest du diese ${documentLabel} wirklich stornieren?\n\n` +
    `Typ: ${billingType}\n` +
    `Dokumenttyp: ${documentLabel}\n` +
    `Betrag: ${formatCurrency(amount)}\n` +
    `Nummer: ${billing.invoiceNumber || billing.reminderInvoiceNumber || 'Vorschau'}\n\n` +
    'Dieser Vorgang kann nicht rückgängig gemacht werden.',
    'Ja, stornieren',
    'Abbrechen',
    {
      icon: 'warning',
      confirmButtonColor: '#6c757d'
    }
  )

  if (!result.isConfirmed) {
    return
  }

  try {
    loading.value = true

    const apiBillingType = billing.type === 'coin' ? 'billing' : 'teamBilling'
    const response = await adminApi.cancelBillingRequest(billing.id, apiBillingType)

    if (response.success) {
      showSuccess(response.message)
      await loadBillings()
    } else {
      showError(response.message || 'Fehler beim Stornieren der Zahlungsaufforderung')
    }
  } catch (error) {
    console.error('Error cancelling billing:', error)
    showError(error.response?.data?.message || 'Fehler beim Stornieren der Zahlungsaufforderung')
  } finally {
    loading.value = false
  }
}

const recheckPayPal = async (billing) => {
  try {
    loading.value = true
    
    const billingType = billing.type === 'coin' ? 'billing' : 'teamBilling'
    const result = await adminApi.recheckPayPalPayment(billing.id, billingType)
    
    if (result.success) {
      showSuccess(result.message)
      await loadBillings()
    } else {
      showError(result.message || 'Fehler beim Überprüfen der PayPal-Zahlung')
    }
  } catch (error) {
    console.error('Error rechecking PayPal:', error)
    showError(error.response?.data?.message || 'Fehler beim Überprüfen der PayPal-Zahlung')
  } finally {
    loading.value = false
  }
}

const deleteBilling = async (billing) => {
  const billingType = billing.type === 'coin' ? 'Quittung-Kauf' : 'Team-Upgrade'
  const amount = billing.price || billing.amount
  const documentLabel = getDocumentLabel(billing)

  const result = await showConfirmDialog(
    'Fehlgeschlagene Rechnung löschen',
    `Möchtest du diese fehlgeschlagene ${documentLabel} wirklich löschen?\n\n` +
    `Typ: ${billingType}\n` +
    `Dokumenttyp: ${documentLabel}\n` +
    `Betrag: ${formatCurrency(amount)}\n` +
    `Nummer: ${billing.invoiceNumber || billing.reminderInvoiceNumber || 'Vorschau'}\n\n` +
    'Dieser Vorgang kann nicht rückgängig gemacht werden.',
    'Ja, löschen',
    'Abbrechen',
    {
      icon: 'warning',
      confirmButtonColor: '#dc3545'
    }
  )

  if (!result.isConfirmed) {
    return
  }

  try {
    loading.value = true

    const apiBillingType = billing.type === 'coin' ? 'billing' : 'teamBilling'
    const response = await adminApi.deleteBilling(billing.id, apiBillingType)

    if (response.success) {
      showSuccess(response.message)
      await loadBillings()
    } else {
      showError(response.message || 'Fehler beim Löschen der Rechnung')
    }
  } catch (error) {
    console.error('Error deleting billing:', error)
    showError(error.response?.data?.message || 'Fehler beim Löschen der Rechnung')
  } finally {
    loading.value = false
  }
}

const getTypeDisplayName = (type) => {
  switch (type) {
    case 'coin':
      return 'Quittung-Kauf'
    case 'team':
      return 'Team-Upgrade'
    default:
      return type
  }
}

const getTypeBadgeClass = (type) => {
  switch (type) {
    case 'coin':
      return 'badge-coin'
    case 'team':
      return 'badge-team'
    default:
      return 'badge-secondary'
  }
}

const getStatusDisplayName = (status) => {
  switch (status) {
    case 'pending':
      return 'Ausstehend'
    case 'completed':
      return 'Abgeschlossen'
    case 'failed':
      return 'Fehlgeschlagen'
    case 'cancelled':
      return 'Storniert'
    default:
      return status
  }
}

const canCancelBilling = (billing) => {
  const paymentMethod = billing.method || billing.paymentMethod
  return paymentMethod === 'bank_transfer' && billing.status === 'pending'
}

const getDocumentLabel = (billing) => {
  if ((billing.method || billing.paymentMethod) === 'bank_transfer' && !billing.payed) {
    return 'Zahlungsaufforderung'
  }
  return 'Rechnung'
}

const getStatusBadgeClass = (status) => {
  switch (status) {
    case 'pending':
      return 'badge-pending'
    case 'completed':
      return 'badge-completed'
    case 'failed':
      return 'badge-failed'
    case 'cancelled':
      return 'badge-cancelled'
    default:
      return 'badge-secondary'
  }
}

const formatCurrency = (amount) => {
  if (!amount) return '€0,00'
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount)
}

const formatNumber = (number) => {
  if (!number) return '0'
  return new Intl.NumberFormat('de-DE').format(number)
}

const getPaymentMethodIcon = (method) => {
  switch (method) {
    case 'coupon':
      return 'fas fa-ticket-alt'
    case 'free':
      return 'fas fa-gift'
    case 'in_app_purchase':
      return 'fas fa-mobile-alt'
    case 'paypal':
      return 'fab fa-paypal'
    case 'bank_transfer':
      return 'fas fa-university'
    default:
      return 'fas fa-question-circle'
  }
}

const getPaymentMethodClass = (method) => {
  switch (method) {
    default:
      return 'text-primary'
  }
}

const getPaymentMethodTitle = (method) => {
  switch (method) {
    case 'coupon':
      return 'Gutschein'
    case 'free':
      return 'Free Coins'
    case 'in_app_purchase':
      return 'In-App-Kauf'
    case 'paypal':
      return 'PayPal'
    case 'bank_transfer':
      return 'Banküberweisung'
    default:
      return 'Unbekannte Zahlungsart'
  }
}

const formatDate = (date) => {
  if (!date) return '-'
  return new Date(date).toLocaleDateString('de-DE')
}

const loadTeams = async () => {
  try {
    const response = await adminApi.getAllTeams({ limit: 1000 })
    teams.value = response.teams || []
  } catch (err) {
    console.error('Error loading teams:', err)
  }
}

watch(
  filters,
  () => {
    if (pagination.value) {
      pagination.value.page = 1
    }
  },
  { deep: true },
)

onMounted(() => {
  loadTeams()
  loadBillings()
})
</script>

<style src="vue-multiselect/dist/vue-multiselect.css"></style>

<style scoped>
</style>
